import React from 'react';
import { Link } from 'react-router-dom';

const EducausePage = () => {
  return (
    <div className="min-h-screen bg-purple-50 pb-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-14">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            EDUCAUSE Conference
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed px-2">
            Join us at EDUCAUSE and experience CampusMind's innovative AI solutions for higher education.
          </p>
        </div>

        {/* Main Content */}
        <div className="space-y-8">
          {/* Pilot Programs Available */}
          <section className="bg-white rounded-xl shadow-lg p-6 sm:p-8 border-l-8 border-blue-500">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Pilot Programs & Demos Available</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              We're excited to offer exclusive pilot programs and live demos for EDUCAUSE attendees. 
              Experience how CampusMind can transform your university's teaching, learning, and administrative workflows.
            </p>

            <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-semibold text-blue-900 mb-3">What You'll Get:</h3>
              <ul className="text-blue-800 space-y-3">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">✓</span>
                  <span><strong>AI Marketplace Access:</strong> Explore and deploy pre-built agents ready to use across academic and administrative workflows.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">✓</span>
                  <span><strong>No-Code Agent Builder:</strong> Create and customize your own agent — no coding required.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">✓</span>
                  <span><strong>Virtual Teaching Assistant Demo:</strong> See how our VTA supports students and faculty with 24/7 course-aware assistance.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">✓</span>
                  <span><strong>Accessibility Agent (Early Access):</strong> Test our AI-powered PDF conversion tool that automatically makes documents WCAG 2.1 AA compliant.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">✓</span>
                  <span><strong>Student Analytics Dashboard:</strong> Spot knowledge gaps and boost engagement with real-time insights.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">✓</span>
                  <span><strong>Compliance-Safe:</strong> FERPA-aligned AI with full security documentation (HECVAT and VPAT available).</span>
                </li>
              </ul>
            </div>

            <div className="text-center space-y-4">
              <a
                href="https://campusmind.ai/exclusive-free-trial-fall/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-purple-700 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-purple-800 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Apply Now →
              </a>
              <div className="mt-4">
                <a
                  href="https://calendly.com/santosh-kumar-royalcyber/educause?month=2025-10"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-blue-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Schedule Meeting Now →
                </a>
              </div>
            </div>
          </section>

          {/* Why CampusMind */}
          <section className="bg-white rounded-xl shadow-lg p-6 sm:p-8 border-l-8 border-purple-500">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Why CampusMind?</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              CampusMind isn't just another AI plugin—it's a purpose-built platform engineered specifically for higher education.
            </p>
            <ul className="text-gray-800 space-y-2 pl-4 sm:pl-6 list-disc">
              <li>Automate repetitive student inquiries and reduce faculty support burden</li>
              <li>Scale personalized learning support without compromising academic integrity</li>
              <li>Deploy in hours—natively within Canvas, with zero disruption to existing workflows</li>
              <li>Natively integrates with your existing LMS systems</li>
              <li>Enterprise-grade security and compliance</li>
            </ul>
          </section>

          {/* 2-Week Proof-of-Value Pilot */}
          <section className="bg-white rounded-xl shadow-lg p-6 sm:p-8 border-l-8 border-green-500">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2-Week Proof-of-Value Pilot</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Before a full rollout, we offer a <strong>2-week Proof-of-Value Pilot</strong> designed to demonstrate real outcomes 
              across teaching, learning, and campus operations.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Apply for up to <strong>3 courses this Fall</strong> and experience the impact firsthand—no cost, no commitment, just results.
            </p>
          </section>

          {/* Exclusive Offer */}
          <section className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-xl shadow-lg p-6 sm:p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">Exclusive Offer for EDUCAUSE Attendees</h2>
            <p className="text-lg leading-relaxed mb-6">
              Full access to CampusMind for 3 courses during your trial. Deep Canvas integration, student analytics, 
              compliance-safe AI, and hands-on support—all included at no cost.
            </p>
            <p className="text-xl font-semibold mb-6">
              No Cost. No Commitment. Just Results.
            </p>
            <a
              href="https://campusmind.ai/exclusive-free-trial-fall/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-white text-purple-700 px-8 py-4 rounded-lg font-bold text-lg hover:bg-purple-50 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Register Now →
            </a>
          </section>
        </div>

        {/* Navigation */}
        <div className="mt-12 flex justify-center gap-4 md:gap-8 flex-wrap">
          <Link
            to="/"
            className="bg-gray-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-700 transition-colors duration-200 shadow-md hover:shadow-lg"
          >
            Back to Start
          </Link>
          <Link
            to="/test"
            className="bg-purple-700 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-800 transition-colors duration-200 shadow-md hover:shadow-lg"
          >
            Explore Labs
          </Link>
        </div>
      </div>
    </div>
  );
};

export default EducausePage;

